import './toast.component.scss';

import { h } from 'preact';

import { IconClose } from '../common/icons/icon-close';
import { useTranslation } from '../i18n';
import type { Toast } from './toast.model';
import { toastService } from './toast.service';

const ToastIcon = ({ type }: { type?: string }) => {
  if (type === 'success') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="M20 6L9 17l-5-5" />
      </svg>
    );
  }
  if (type === 'error') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    );
  }
  return <span class="toast-icon-glyph">!</span>;
};

// Measured at render (each signal change re-renders while toasts are live):
// desktop toasts sit just below the theme header, whatever theme and height;
// mobile toasts sit just above the mobile stack, however tall it currently
// is (nav + player + comment bar).
const getPlacement = () => {
  if (window.innerWidth <= 530) {
    // Sit just above the stack's top edge with the stack's own internal gap
    // (.25em), so the toast reads as one more stacked bar
    const stackTop = document.getElementById('single-mobile-stack')?.getBoundingClientRect().top;
    const bottom = stackTop && stackTop > 0 ? Math.round(window.innerHeight - stackTop + 4) : 76;
    return { bottom: `${bottom}px` };
  }
  const header = document.querySelector(
    '#shopify-section-header, .shopify-section-group-header-group, .section-header, header'
  );
  const headerBottom = header?.getBoundingClientRect().bottom ?? 0;
  // Clamp: a scrolled-away header reports a negative bottom, and an
  // unexpectedly tall match shouldn't push toasts mid-page.
  const top = Math.max(20, Math.min(Math.round(headerBottom + 12), 220));
  return { top: `${top}px` };
};

export const Toasts = () => {
  const { t } = useTranslation();
  const toasts = toastService.toasts.value;

  if (!toasts.length) return null;

  return (
    <ul class="notifications" style={getPlacement()}>
      {toasts.map((toast: Toast, index: number) => (
        <li
          key={index}
          class={`toast ${toast.className} ${toast.hiding ? 'hide' : ''}`}
          style={{ '--toast-duration': `${toast.delay ?? 5000}ms` }}
          role="status"
        >
          <span class="toast-icon" aria-hidden="true">
            <ToastIcon type={toast.className} />
          </span>
          <div class="toast-content">
            {toast.header && <h4>{toast.header}</h4>}
            {toast.body && <p>{toast.body}</p>}
          </div>
          <button
            type="button"
            class="toast-close"
            aria-label={t('common.close')}
            onClick={() => toastService.remove(toast)}
          >
            <IconClose width={16} height={16} />
          </button>
        </li>
      ))}
    </ul>
  );
};
